import React, { useState, useEffect, lazy } from 'react';
import { useDispatch } from 'react-redux';
import { fetchAssessmentProduct } from 'actions/counselor';
import { setLocalSignInRedirect } from 'actions/app';
import storage from 'helpers/storage';
import getLocale from 'helpers/getLocale';
import { formatCurrencyDisplay } from 'helpers/currency';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import { styled, keyframes } from '@mui/system';
import {
  Building2, Phone, Award, Check,
  UserPlus, Shuffle, GraduationCap,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';
import { submitContactForm } from 'actions/contact';
import { addMessage, showServiceMessage } from 'actions/error';
import config from 'config';
import Bg1 from 'pages/Vacancy/assets/bg1.svg?react';

import SEO from 'components/SEO';

const MainLayout = lazy(() => import('layouts/MainLayout'));
const Header = lazy(() => import('components/Header'));
const Footer = lazy(() => import('components/Footer'));
const Breadcrumbs = lazy(() => import('components/BreadCrumbs'));

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0);    }
`;

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------

// Horizontal padding longhands only — putting `[breakpoints.down(x)]` keys
// here causes JS duplicate-key collisions when callers also declare those
// breakpoints, and `padding: '0 X'` shorthand resets paddingTop/paddingBottom.
// Each *Inner declares its own responsive padding in one place below.
const wrapStyle = () => ({
  maxWidth: 1440,
  margin: '0 auto',
  boxSizing: 'border-box',
  width: '100%',
  paddingLeft: 100,
  paddingRight: 100,
});

// ---------------------------------------------------------------------------
// Shared section styles
// ---------------------------------------------------------------------------

const DarkSection = styled('section')(() => ({
  backgroundColor: 'rgba(34,34,34,1)',
}));

const OffWhiteSection = styled('section')(() => ({
  backgroundColor: '#FAFAFA',
}));

const WhiteSection = styled('section')(() => ({
  backgroundColor: '#fff',
}));

const SectionInner = styled('div')(({ theme }) => ({
  ...wrapStyle(),
  paddingTop: 90,
  paddingBottom: 90,
  [theme.breakpoints.down('lg')]: { paddingLeft: 50, paddingRight: 50 },
  [theme.breakpoints.down('md')]: { paddingTop: 60, paddingBottom: 60, paddingLeft: 30, paddingRight: 30 },
  [theme.breakpoints.down('sm')]: { paddingTop: 48, paddingBottom: 48, paddingLeft: 20, paddingRight: 20 },
}));

const SectionEyebrow = styled(Typography, {
  shouldForwardProp: (p) => p !== 'dark',
})(({ dark }) => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: 2,
  textTransform: 'uppercase',
  color: dark ? 'rgba(185,248,51,1)' : 'rgba(121,201,18,1)',
  marginBottom: 14,
}));

const SectionTitle = styled(Typography, {
  shouldForwardProp: (p) => p !== 'dark' && p !== 'large',
})(({ theme, dark, large }) => ({
  fontFamily: 'Unbounded',
  fontSize: large ? 42 : 34,
  lineHeight: large ? '54px' : '46px',
  fontWeight: 700,
  color: dark ? '#fff' : 'rgba(34,34,34,1)',
  marginBottom: 16,
  maxWidth: 720,
  [theme.breakpoints.down('md')]: {
    fontSize: large ? 32 : 26,
    lineHeight: large ? '40px' : '36px',
  },
}));

const SectionSubtitle = styled(Typography, {
  shouldForwardProp: (p) => p !== 'dark',
})(({ dark }) => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 17,
  lineHeight: '28px',
  color: dark ? 'rgba(255,255,255,0.6)' : 'rgba(34,34,34,0.6)',
  maxWidth: 620,
  marginBottom: 52,
}));

// ---------------------------------------------------------------------------
// Hero (UNCHANGED — preserved per scope)
// ---------------------------------------------------------------------------

const HeroSection = styled('section')(() => ({
  position: 'relative',
  background: '#fdfef9',
  overflow: 'hidden',
}));

const HeroInner = styled('div')(({ theme }) => ({
  padding: '40px 100px',
  ...theme.wrapper,
  boxSizing: 'border-box',
  position: 'relative',
  paddingBottom: 40,
  [theme.breakpoints.down('lg')]: { padding: 50 },
  [theme.breakpoints.down('md')]: { padding: 30 },
  [theme.breakpoints.down('sm')]: { padding: 20, paddingBottom: 30 },
}));

const HeroBg1 = styled(Bg1)(({ theme }) => ({
  position: 'absolute',
  left: '35%',
  top: -115,
  width: 1000,
  zIndex: 0,
  [theme.breakpoints.down('md')]: { display: 'none' },
}));

const HeroFlex = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  flexDirection: 'row',
  gap: 100,
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.down('md')]: { gap: 60 },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: 15,
  },
}));

const HeroImage = styled('img')(({ theme }) => ({
  width: '100%',
  maxWidth: 370,
  marginBottom: 40,
  [theme.breakpoints.down('sm')]: { marginBottom: 0 },
}));

const HeroEyebrow = styled(Typography, {
  shouldForwardProp: (p) => p !== 'visible',
})(({ visible }) => ({
  fontFamily: 'Rubik-Regular',
  display: 'inline-flex',
  alignItems: 'flex-start',
  gap: 8,
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 600,
  letterSpacing: 2,
  textTransform: 'uppercase',
  color: 'rgba(34, 34, 34, 0.5)',
  marginBottom: 16,
  animation: visible ? `${fadeInUp} 0.7s ease-out 0.1s both` : 'none',
  '& > svg': { flexShrink: 0, marginTop: 1 },
}));

const HeroTitle = styled(Typography, {
  shouldForwardProp: (p) => p !== 'visible',
})(({ theme, visible }) => ({
  fontFamily: 'Unbounded',
  marginTop: 40,
  marginBottom: 32,
  fontSize: 46,
  lineHeight: '56px',
  fontWeight: 700,
  maxWidth: 900,
  color: 'rgba(34,34,34,1)',
  animation: visible ? `${fadeInUp} 0.7s ease-out 0.25s both` : 'none',
  '& em': { fontStyle: 'normal', color: 'rgba(121,201,18,1)', fontFamily: 'Unbounded' },
  [theme.breakpoints.down('md')]: { fontSize: 32, lineHeight: '40px', marginBottom: 20 },
  [theme.breakpoints.down('sm')]: { fontSize: 24, lineHeight: '32px', marginBottom: 20 },
}));

const HeroSubtitle = styled(Typography, {
  shouldForwardProp: (p) => p !== 'visible',
})(({ theme, visible }) => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 16,
  lineHeight: '22px',
  color: 'rgba(34,34,34,0.85)',
  marginTop: 10,
  marginBottom: 10,
  animation: visible ? `${fadeInUp} 0.7s ease-out 0.4s both` : 'none',
  [theme.breakpoints.down('sm')]: { fontSize: 16, lineHeight: '22px' },
}));

const HeroCTARow = styled('div', {
  shouldForwardProp: (p) => p !== 'visible',
})(({ visible }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 20,
  flexWrap: 'wrap',
  animation: visible ? `${fadeInUp} 0.7s ease-out 0.55s both` : 'none',
}));

const HeroTextColumn = styled('div')(({ theme }) => ({
  marginTop: -80,
  [theme.breakpoints.down('sm')]: { marginTop: 0 },
}));

// ---------------------------------------------------------------------------
// Methodology strip (replaces yellow stats)
// ---------------------------------------------------------------------------

const MethodSection = styled('section')(() => ({
  backgroundColor: '#fff',
  borderTop: '1px solid rgba(34,34,34,0.08)',
  borderBottom: '1px solid rgba(34,34,34,0.08)',
}));

const MethodInner = styled('div')(({ theme }) => ({
  ...wrapStyle(),
  paddingTop: 36,
  paddingBottom: 36,
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 48,
  [theme.breakpoints.down('lg')]: { paddingLeft: 50, paddingRight: 50 },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: 28,
    paddingTop: 48,
    paddingBottom: 48,
    paddingLeft: 30,
    paddingRight: 30,
  },
  [theme.breakpoints.down('sm')]: { paddingLeft: 20, paddingRight: 20 },
}));

const MethodCol = styled('div')(() => ({}));

const MethodColTitle = styled(Typography)(() => ({
  fontFamily: 'Unbounded',
  fontSize: 18,
  fontWeight: 700,
  color: 'rgba(34,34,34,1)',
  marginBottom: 8,
  lineHeight: '24px',
}));

const MethodColItems = styled(Typography)(() => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 14,
  color: 'rgba(34,34,34,0.55)',
  lineHeight: '22px',
  '& em': {
    fontStyle: 'normal',
    color: 'rgba(121,201,18,1)',
    fontWeight: 600,
  },
}));

// ---------------------------------------------------------------------------
// Sample Report (centerpiece) — large title, two-column
// ---------------------------------------------------------------------------

const SampleGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 80,
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: 40,
  },
}));

const SampleBullets = styled('ul')(() => ({
  listStyle: 'none',
  padding: 0,
  margin: '0 0 36px 0',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
}));

const SampleBullet = styled('li')(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  fontSize: 15,
  lineHeight: '22px',
  color: 'rgba(34,34,34,0.75)',
  fontFamily: 'Rubik-Regular',
}));

const SampleCheckIcon = styled('span')(() => ({
  flexShrink: 0,
  width: 22,
  height: 22,
  borderRadius: '50%',
  backgroundColor: 'rgba(121,201,18,0.12)',
  color: 'rgba(121,201,18,1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: 1,
}));

const SampleCTARow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 24,
  flexWrap: 'wrap',
}));

const SampleSecondaryLink = styled('a')(() => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 14,
  fontWeight: 500,
  color: 'rgba(34,34,34,0.7)',
  textDecoration: 'none',
  borderBottom: '1px solid rgba(34,34,34,0.2)',
  paddingBottom: 2,
  cursor: 'pointer',
  transition: 'color 0.2s, border-color 0.2s',
  '&:hover': {
    color: 'rgba(121,201,18,1)',
    borderColor: 'rgba(121,201,18,1)',
  },
}));

// Visual report mock — gives the page a strong "show the product" moment
// without depending on a screenshot asset to be produced.
const ReportFrame = styled('div')(({ theme }) => ({
  position: 'relative',
  background: '#fff',
  borderRadius: 16,
  border: '1px solid rgba(0,0,0,0.07)',
  boxShadow:
    '0 30px 60px -20px rgba(34,34,34,0.18), 0 12px 24px -12px rgba(34,34,34,0.08)',
  padding: 36,
  transform: 'rotate(-1.5deg)',
  transition: 'transform 0.6s ease',
  '&:hover': { transform: 'rotate(0deg)' },
  [theme.breakpoints.down('sm')]: { padding: 24 },
}));

const ReportLabel = styled('div')(() => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: 1.4,
  textTransform: 'uppercase',
  color: 'rgba(34,34,34,0.4)',
  marginBottom: 8,
}));

const ReportName = styled('div')(() => ({
  fontFamily: 'Unbounded',
  fontSize: 18,
  fontWeight: 700,
  color: 'rgba(34,34,34,1)',
  marginBottom: 24,
}));

const ReportSection = styled('div')(() => ({
  marginBottom: 22,
}));

const ReportSectionLabel = styled('div')(() => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 1.2,
  textTransform: 'uppercase',
  color: 'rgba(34,34,34,0.45)',
  marginBottom: 10,
}));

const RadarMock = styled('div')(() => ({
  position: 'relative',
  width: 140,
  height: 140,
  margin: '0 auto',
  borderRadius: '50%',
  background:
    'radial-gradient(circle at center, rgba(185,248,51,0.18) 0%, rgba(185,248,51,0.04) 60%, transparent 100%)',
  border: '1px dashed rgba(34,34,34,0.12)',
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '100%',
    height: 1,
    backgroundColor: 'rgba(34,34,34,0.1)',
    transform: 'translate(-50%, -50%)',
  },
  '&::after': {
    width: 1,
    height: '100%',
  },
}));

const RadarShape = styled('div')(() => ({
  position: 'absolute',
  inset: 18,
  background: 'rgba(121,201,18,0.35)',
  clipPath:
    'polygon(50% 0%, 92% 28%, 78% 88%, 30% 95%, 8% 50%, 24% 12%)',
  border: '1px solid rgba(121,201,18,0.6)',
  borderRadius: '50%',
  filter: 'blur(0.4px)',
}));

const BarRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginBottom: 8,
}));

const BarLabel = styled('div')(() => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 12,
  color: 'rgba(34,34,34,0.7)',
  width: 90,
  flexShrink: 0,
}));

// Progress bar — track + fill render as a 6 px pill. Using 999 instead of
// half-height (3) so the pill stays visually correct if the height ever
// grows, and the magic-3 doesn't get confused with the r=8 button family.
const BarTrack = styled('div')(() => ({
  flex: 1,
  height: 6,
  background: 'rgba(34,34,34,0.06)',
  borderRadius: 25,
  overflow: 'hidden',
}));

const BarFill = styled('div', {
  shouldForwardProp: (p) => p !== 'pct' && p !== 'tone',
})(({ pct, tone }) => ({
  height: '100%',
  width: `${pct}%`,
  background:
    tone === 'lime' ? 'rgba(121,201,18,0.85)' :
      tone === 'mid' ? 'rgba(34,34,34,0.55)' :
        'rgba(34,34,34,0.3)',
  borderRadius: 25,
  transition: 'width 0.6s ease',
}));

const ReportFooter = styled('div')(() => ({
  borderTop: '1px solid rgba(34,34,34,0.08)',
  paddingTop: 14,
  marginTop: 14,
  fontFamily: 'Rubik-Regular',
  fontSize: 11,
  color: 'rgba(34,34,34,0.4)',
  display: 'flex',
  justifyContent: 'space-between',
}));

// ---------------------------------------------------------------------------
// Dimensions (reframed)
// ---------------------------------------------------------------------------

const DimensionsGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 20,
  [theme.breakpoints.down('md')]: { gridTemplateColumns: 'repeat(2, 1fr)' },
  [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
}));

const DimensionCard = styled('div')(() => ({
  borderRadius: 16,
  padding: '32px 28px',
  backgroundColor: '#fff',
  border: '1px solid rgba(0,0,0,0.07)',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  transition: 'border-color 0.2s, transform 0.2s',
  '&:hover': {
    borderColor: 'rgba(121,201,18,0.4)',
    transform: 'translateY(-2px)',
  },
}));

const VizFrame = styled('div')(() => ({
  width: 56,
  height: 56,
  borderRadius: 12,
  backgroundColor: 'rgba(121,201,18,0.08)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 6,
  padding: 10,
  boxSizing: 'border-box',
  alignSelf: 'flex-start',
  flexShrink: 0,
}));

function DimensionViz({ type }) {
  const lime = 'rgba(121,201,18,1)';
  const limeMid = 'rgba(121,201,18,0.55)';
  const dim = 'rgba(34,34,34,0.18)';

  if (type === 'bars') {
    const heights = [22, 14, 30, 18, 26, 10];
    return (
      <svg viewBox="0 0 80 36" width="100%" height="36" preserveAspectRatio="xMidYMid meet">
        {heights.map((h, i) => (
          <rect key={i} x={3 + i * 13} y={34 - h} width="9" height={h}
            fill={i < 2 ? lime : i < 4 ? limeMid : dim} rx="1.5" />
        ))}
      </svg>
    );
  }

  if (type === 'gauge') {
    return (
      <svg viewBox="0 0 80 36" width="100%" height="36" preserveAspectRatio="xMidYMid meet">
        <path d="M14 32 A 22 22 0 0 1 66 32" fill="none" stroke={dim} strokeWidth="3" strokeLinecap="round" />
        <path d="M14 32 A 22 22 0 0 1 50 8" fill="none" stroke={lime} strokeWidth="3" strokeLinecap="round" />
        <circle cx="40" cy="32" r="3" fill={lime} />
        <line x1="40" y1="32" x2="48" y2="14" stroke={lime} strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  if (type === 'spark-up') {
    return (
      <svg viewBox="0 0 80 36" width="100%" height="36" preserveAspectRatio="xMidYMid meet">
        <polyline points="4,30 16,24 28,26 40,18 52,12 64,8 76,4"
          fill="none" stroke={lime} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="76" cy="4" r="3" fill={lime} />
      </svg>
    );
  }

  if (type === 'radar') {
    return (
      <svg viewBox="0 0 80 36" width="100%" height="36" preserveAspectRatio="xMidYMid meet">
        <polygon points="40,4 60,14 53,30 27,30 20,14"
          fill="none" stroke={dim} strokeWidth="0.7" strokeDasharray="2 2" />
        <polygon points="40,10 54,18 50,28 30,28 26,18"
          fill="rgba(121,201,18,0.28)" stroke={lime} strokeWidth="1.4" strokeLinejoin="round" />
        <circle cx="40" cy="10" r="1.6" fill={lime} />
        <circle cx="54" cy="18" r="1.6" fill={lime} />
        <circle cx="50" cy="28" r="1.6" fill={lime} />
        <circle cx="30" cy="28" r="1.6" fill={lime} />
        <circle cx="26" cy="18" r="1.6" fill={lime} />
      </svg>
    );
  }

  if (type === 'dot-grid') {
    const dots = [
      [12, 12, 4.5, 1], [26, 12, 3, 0.6], [40, 12, 5.5, 1], [54, 12, 2.5, 0.4],
      [12, 26, 2.5, 0.4], [26, 26, 4, 0.7], [40, 26, 3, 0.5], [54, 26, 5, 0.95],
    ];
    return (
      <svg viewBox="0 0 80 36" width="100%" height="36" preserveAspectRatio="xMidYMid meet">
        {dots.map(([x, y, r, opacity], i) => (
          <circle key={i} cx={x} cy={y} r={r} fill={lime} opacity={opacity} />
        ))}
      </svg>
    );
  }

  if (type === 'bell') {
    return (
      <svg viewBox="0 0 80 36" width="100%" height="36" preserveAspectRatio="xMidYMid meet">
        <path d="M4 32 Q 18 32, 24 22 T 40 6 T 56 22 T 76 32"
          fill="rgba(121,201,18,0.12)" stroke="none" />
        <path d="M4 32 Q 18 32, 24 22 T 40 6 T 56 22 T 76 32"
          fill="none" stroke={lime} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="40" y1="6" x2="40" y2="32" stroke={dim} strokeWidth="0.7" strokeDasharray="2 2" />
      </svg>
    );
  }

  return null;
}

const DimensionTitle = styled(Typography)(() => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 17,
  fontWeight: 700,
  color: 'rgba(34,34,34,1)',
  lineHeight: '24px',
}));

const DimensionText = styled(Typography)(() => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 14,
  lineHeight: '22px',
  color: 'rgba(34,34,34,0.6)',
  flex: 1,
}));

const DimensionFramework = styled('span')(() => ({
  display: 'inline-block',
  marginTop: 8,
  fontFamily: 'Rubik-Regular',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 1.2,
  textTransform: 'uppercase',
  color: 'rgba(121,201,18,1)',
}));

// ---------------------------------------------------------------------------
// Session scenarios
// ---------------------------------------------------------------------------

const ScenarioGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 24,
  [theme.breakpoints.down('md')]: { gridTemplateColumns: '1fr' },
}));

const ScenarioCard = styled('div')(() => ({
  borderRadius: 16,
  padding: '36px 32px',
  background: '#fff',
  border: '1px solid rgba(0,0,0,0.06)',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
}));

const ScenarioIconWrap = styled('div')(() => ({
  width: 48,
  height: 48,
  borderRadius: 14,
  backgroundColor: 'rgba(34,34,34,0.04)',
  color: 'rgba(34,34,34,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 8,
}));

const ScenarioTitle = styled(Typography)(() => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 17,
  fontWeight: 700,
  color: 'rgba(34,34,34,1)',
  lineHeight: '24px',
}));

const ScenarioText = styled(Typography)(() => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 14,
  lineHeight: '22px',
  color: 'rgba(34,34,34,0.65)',
}));

// ---------------------------------------------------------------------------
// Steps (How it works) — dark canyon kept; numerals desaturated
// ---------------------------------------------------------------------------

const StepsGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 24,
  [theme.breakpoints.down('md')]: { gridTemplateColumns: '1fr' },
}));

const StepCard = styled('div')(() => ({
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 16,
  padding: '36px 32px',
}));

const StepNumber = styled(Typography)(() => ({
  fontFamily: 'Unbounded',
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: 1.5,
  color: 'rgba(185,248,51,1)',
  marginBottom: 16,
}));

const StepTitle = styled(Typography)(() => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 18,
  fontWeight: 600,
  color: '#fff',
  marginBottom: 10,
  lineHeight: '26px',
}));

const StepText = styled(Typography)(() => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 15,
  lineHeight: '24px',
  color: 'rgba(255,255,255,0.5)',
}));

const StepsFooter = styled(Typography)(() => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 14,
  color: 'rgba(255,255,255,0.4)',
  marginTop: 36,
  textAlign: 'center',
}));

// ---------------------------------------------------------------------------
// Pricing (two-column: retail-vs-counselor narrative + price card)
// ---------------------------------------------------------------------------

const PricingGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 440px',
  gap: 80,
  alignItems: 'start',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: 40,
  },
}));

const PricingLeftBody = styled(Typography)(() => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 16,
  lineHeight: '26px',
  color: 'rgba(34,34,34,0.65)',
  marginBottom: 24,
}));

const PricingLeftLink = styled('span')(() => ({
  display: 'inline-block',
  fontFamily: 'Rubik-Regular',
  fontSize: 14,
  fontWeight: 600,
  color: 'rgba(121,201,18,1)',
  cursor: 'pointer',
  borderBottom: '1px solid rgba(121,201,18,0.35)',
  paddingBottom: 2,
  transition: 'border-color 0.2s',
  '&:hover': { borderColor: 'rgba(121,201,18,1)' },
}));

const PriceCard = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: 440,
  backgroundColor: '#fff',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 24,
  padding: '44px 40px',
  textAlign: 'center',
  boxShadow: '0 20px 50px -28px rgba(34,34,34,0.18)',
  [theme.breakpoints.down('sm')]: { padding: '32px 24px' },
}));

const PriceAmount = styled(Typography)(({ theme }) => ({
  fontFamily: 'Unbounded',
  fontSize: 56,
  lineHeight: 1,
  fontWeight: 700,
  color: 'rgba(34,34,34,1)',
  marginBottom: 8,
  [theme.breakpoints.down('sm')]: { fontSize: 44 },
}));

const PriceUnit = styled(Typography)(() => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 14,
  color: 'rgba(34,34,34,0.5)',
  marginBottom: 28,
}));

const PriceList = styled('ul')(() => ({
  listStyle: 'none',
  padding: 0,
  margin: '0 0 28px 0',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  textAlign: 'left',
}));

const PriceListItem = styled('li')(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  fontFamily: 'Rubik-Regular',
  fontSize: 14,
  lineHeight: '22px',
  color: 'rgba(34,34,34,0.7)',
}));

const PriceSecondaryRow = styled('div')(() => ({
  marginTop: 16,
  fontFamily: 'Rubik-Regular',
  fontSize: 13,
  color: 'rgba(34,34,34,0.55)',
}));

// ---------------------------------------------------------------------------
// Bottom CTA
// ---------------------------------------------------------------------------

const CTASection = styled('section')(() => ({
  background: 'linear-gradient(135deg, rgba(185,248,51,0.14) 0%, rgba(121,201,18,0.07) 100%)',
  borderTop: '1px solid rgba(121,201,18,0.15)',
}));

const CTAInner = styled('div')(({ theme }) => ({
  ...wrapStyle(),
  paddingTop: 100,
  paddingBottom: 100,
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  [theme.breakpoints.down('lg')]: { paddingLeft: 50, paddingRight: 50 },
  [theme.breakpoints.down('md')]: { paddingTop: 60, paddingBottom: 60, paddingLeft: 30, paddingRight: 30 },
  [theme.breakpoints.down('sm')]: { paddingTop: 48, paddingBottom: 48, paddingLeft: 20, paddingRight: 20 },
}));

const CTATitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Unbounded',
  fontSize: 36,
  lineHeight: '50px',
  fontWeight: 700,
  color: 'rgba(34,34,34,1)',
  marginBottom: 16,
  maxWidth: 720,
  [theme.breakpoints.down('md')]: { fontSize: 26, lineHeight: '36px' },
}));

const CTASubtitle = styled(Typography)(() => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 17,
  lineHeight: '28px',
  color: 'rgba(34,34,34,0.6)',
  marginBottom: 36,
  maxWidth: 500,
}));

const CTASecondaryRow = styled(Typography)(() => ({
  marginTop: 20,
  fontFamily: 'Rubik-Regular',
  fontSize: 14,
  color: 'rgba(34,34,34,0.5)',
  '& a': {
    color: 'rgba(34,34,34,0.75)',
    textDecoration: 'underline',
    cursor: 'pointer',
    marginLeft: 6,
    '&:hover': { color: 'rgba(121,201,18,1)' },
  },
}));

// ---------------------------------------------------------------------------
// Primary action button (replaces CreepyButton on this page)
// ---------------------------------------------------------------------------

const PrimaryActionButton = styled(Button)(() => ({
  // Salad-lime fill + dark-green hover, matching the global contained-
  // primary variant. Font size / family / weight inherited from the theme
  // (Rubik-Regular, 18 px, 500) so this button matches "Замовити демо"
  // at the top of the page. Padding kept custom (14×28) for a slightly
  // larger section-level CTA footprint.
  backgroundColor: 'rgba(185,248,51,1)',
  color: 'rgba(34,34,34,1)',
  textTransform: 'none',
  borderRadius: 8,
  padding: '14px 28px',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: 'rgba(121,201,18,1)',
    boxShadow: 'none',
  },
}));

// ---------------------------------------------------------------------------
// License purchase dialog (unchanged — secondary path)
// ---------------------------------------------------------------------------

const TextFieldStyled = styled(TextField)(() => ({
  backgroundColor: '#fff',
  marginBottom: 20,
  '& .MuiInputBase-input': { height: 'unset' },
  // Lift the shrunk label fully above the input border. Without an explicit
  // transform the label sits at the border line and Cyrillic ascenders/the
  // apostrophe in "Ім'я" get visually clipped. Hiding the notch legend
  // keeps the white-background label visually clean over the outline.
  '& .MuiInputLabel-root.MuiInputLabel-shrink': {
    backgroundColor: '#fff',
    padding: '0 6px',
    transform: 'translate(12px, -9px) scale(0.75)',
  },
  '& .MuiOutlinedInput-notchedOutline legend': {
    maxWidth: 0,
  },
}));

const VOLUME_OPTIONS = [
  { value: '1-9', label: '1–9' },
  { value: '10-49', label: '10–49' },
  { value: '50-99', label: '50–99' },
  { value: '100-499', label: '100–499' },
];

function LicensePurchaseDialog({ open, onClose }) {
  const t = useTranslate('AssessmentLanding');
  const dispatch = useDispatch();

  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [org, setOrg] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [volume, setVolume] = React.useState('1-9');
  const [errors, setErrors] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);

  const validate = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const next = {};
    if (!name.trim()) next.name = 'required';
    else if (name.trim().length < 2) next.name = 'too_short';
    if (!email.trim()) next.email = 'required';
    else if (!emailRegex.test(email.trim())) next.email = 'invalid';
    if (!phone.trim()) next.phone = 'required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    const messageBody = [
      `${t('dialogLicenseInquiry')}`,
      `${t('dialogFieldPhone')}: ${phone.trim()}`,
      `${t('dialogFieldVolume')}: ${volume}`,
      org.trim() ? `${t('dialogFieldOrg')}: ${org.trim()}` : null,
    ]
      .filter(Boolean)
      .join('\n');

    try {
      await dispatch(
        submitContactForm({ name: name.trim(), email: email.trim(), message: messageBody })
      );
      setName(''); setEmail(''); setOrg(''); setPhone('');
      setErrors({});
      onClose();
      dispatch(addMessage({ message: t('dialogSuccess'), variant: 'success' }));
    } catch (err) {
      dispatch(showServiceMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const helperText = (field) => {
    if (!errors[field]) return '';
    if (errors[field] === 'required') return t('fieldRequired');
    if (errors[field] === 'too_short') return t('fieldTooShort');
    if (errors[field] === 'invalid') return t('fieldInvalidEmail');
    return errors[field];
  };

  return (
    <Dialog open={open} onClose={onClose} scroll="body" fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 0, pt: 3, px: 3, fontFamily: 'Unbounded', fontSize: 20 }}>
        {t('dialogTitle')}
      </DialogTitle>
      <Typography sx={{ px: 3, pb: 2, pt: 1, fontSize: 14, lineHeight: '20px', color: 'rgba(34,34,34,0.55)' }}>
        {t('dialogSubtitle')}
      </Typography>
      <form onSubmit={handleSubmit} noValidate>
        <DialogContent sx={{ px: 3, pt: 0, pb: 0 }}>
          <TextFieldStyled
            label={t('dialogFieldName')}
            value={name}
            onChange={(e) => { setName(e.target.value); if (errors.name) setErrors((p) => ({ ...p, name: '' })); }}
            fullWidth error={Boolean(errors.name)} helperText={helperText('name')}
          />
          <TextFieldStyled
            label={t('dialogFieldEmail')}
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors((p) => ({ ...p, email: '' })); }}
            fullWidth error={Boolean(errors.email)} helperText={helperText('email')}
          />
          <TextFieldStyled
            label={t('dialogFieldOrg')}
            value={org}
            onChange={(e) => { setOrg(e.target.value); if (errors.org) setErrors((p) => ({ ...p, org: '' })); }}
            fullWidth error={Boolean(errors.org)} helperText={helperText('org')}
            slotProps={{
              input: { startAdornment: <Building2 size={16} style={{ marginRight: 8, color: 'rgba(0,0,0,0.38)' }} /> }
            }}
          />
          <TextFieldStyled
            label={t('dialogFieldPhone')}
            value={phone}
            onChange={(e) => { setPhone(e.target.value); if (errors.phone) setErrors((p) => ({ ...p, phone: '' })); }}
            fullWidth error={Boolean(errors.phone)} helperText={helperText('phone')}
            slotProps={{
              input: { startAdornment: <Phone size={16} style={{ marginRight: 8, color: 'rgba(0,0,0,0.38)' }} /> }
            }}
          />
          <TextFieldStyled
            select label={t('dialogFieldVolume')}
            value={volume}
            onChange={(e) => setVolume(e.target.value)}
            fullWidth
          >
            {VOLUME_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label} {t('dialogAssessments')}
              </MenuItem>
            ))}
          </TextFieldStyled>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'space-between' }}>
          <Button
            onClick={onClose}
            disabled={submitting}
            sx={{
              color: 'rgba(34,34,34,0.5)',
              fontWeight: 400,
              fontSize: 13,
              textTransform: 'none',
              textDecoration: 'underline',
              minWidth: 'auto',
              padding: 0
            }}>
            {t('dialogCancel')}
          </Button>
          {/*
            Submit ("Надіслати запит") inherits the contained-primary theme
            variant: salad fill, dark-green hover, radius 8, weight 500.
            `color="primary"` is the explicit hook into the variant; we
            don't restate bg/color/radius/weight here.
          */}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={submitting}>
            {submitting ? t('dialogSending') : t('dialogSend')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function AssessmentLandingPage() {
  const t = useTranslate('AssessmentLanding');
  const dispatch = useDispatch();
  const [visible, setVisible] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Single-assessment retail price lives in the `assessment-products`
  // catalog (`assessment360` → `single` pack). The landing page renders
  // it via t('pricingPrice', { price, currency }) and
  // t('pricingLeftBody', { price, currency }). 1499/UAH fallback matches
  // the canonical seed price and keeps the page sensible during the
  // catalog fetch and on network failures.
  const [price, setPrice] = useState(1499);
  const [currency, setCurrency] = useState('UAH');

  useEffect(() => {
    const id = setTimeout(() => setVisible(true), 80);
    // Tag visitors as counselor-funnel registrants. The API client reads
    // this on every request as X-Source; the backend grants the
    // `consultant` role at first-time user creation. No-op on subsequent
    // logged-in calls.
    try {
      sessionStorage.setItem('registrationSource', 'pro');
    } catch (_) {
      // sessionStorage unavailable — fall back to post-payment role grant.
    }
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    dispatch(fetchAssessmentProduct('assessment360')).then((res) => {
      if (cancelled || !res?.data) return;
      const single = (res.data.packs || []).find((p) => p.id === 'single');
      if (single?.price?.amount) {
        setPrice(single.price.amount);
        setCurrency(single.price.currency || 'UAH');
      }
    });
    return () => { cancelled = true; };
  }, [dispatch]);

  const navigate = useNavigate();
  const openDialog = () => setDialogOpen(true);
  const closeDialog = () => setDialogOpen(false);
  const goToPurchase = () => navigate('/assessment/purchase');

  // "Пройти тест на собі" funnel: register → basic-details form → free
  // voucher. The redirect is persisted to storage so it survives the
  // email-verification round trip (Redux state is lost across it). The
  // path is stored unlocalized — SignIn/SignUp localize it on use.
  const goToProRegistration = () => {
    dispatch(setLocalSignInRedirect('/pro/start'));
    try {
      storage.setItem('postAuthRedirect', '/pro/start');
    } catch (_) {
      // storage unavailable — the Redux redirect still covers the
      // same-session (Google) path.
    }
    navigate('/sign-up?source=pro');
  };

  const DIMENSIONS = [
    { viz: 'bars', title: t('dim1Title'), text: t('dim1Text'), framework: t('dim1Framework') },
    { viz: 'gauge', title: t('dim2Title'), text: t('dim2Text'), framework: t('dim2Framework') },
    { viz: 'spark-up', title: t('dim3Title'), text: t('dim3Text'), framework: t('dim3Framework') },
    { viz: 'radar', title: t('dim4Title'), text: t('dim4Text'), framework: t('dim4Framework') },
    { viz: 'dot-grid', title: t('dim5Title'), text: t('dim5Text'), framework: t('dim5Framework') },
    { viz: 'bell', title: t('dim6Title'), text: t('dim6Text'), framework: t('dim6Framework') },
  ];

  const SCENARIOS = [
    { Icon: UserPlus, title: t('scenario1Title'), text: t('scenario1Text') },
    { Icon: Shuffle, title: t('scenario2Title'), text: t('scenario2Text') },
    { Icon: GraduationCap, title: t('scenario3Title'), text: t('scenario3Text') },
  ];

  const STEPS = [
    { n: '01', title: t('step1Title'), text: t('step1Text') },
    { n: '02', title: t('step2Title'), text: t('step2Text') },
    { n: '03', title: t('step3Title'), text: t('step3Text') },
  ];

  return (
    <MainLayout scrollToTop={false}>
      <SEO page="assessment" includeHreflang />
      <Header />

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <HeroSection>
        <HeroInner>
          <HeroBg1 />

          <Breadcrumbs
            breadcrumbs={[
              { label: 'Home', link: '/' },
              { label: t('breadcrumb'), raw: true }
            ]}
          />

          <HeroTitle component="h1" visible={visible}
            dangerouslySetInnerHTML={{ __html: t('heroTitle') }}
          />

          <HeroFlex>
            <HeroImage
              src={`${config.imageSource}/151123fd-b062-45f2-dc7a-5a8bf7251e00/public`}
              alt={t('heroPhotoAlt')}
            />

            <HeroTextColumn>
              <HeroEyebrow visible={visible} component="p">
                <Award size={14} />
                {t('eyebrow')}
              </HeroEyebrow>

              <HeroSubtitle visible={visible}>
                {t('heroSubtitle')}
              </HeroSubtitle>

              <HeroCTARow visible={visible}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={openDialog}
                  sx={{ marginTop: 2, borderRadius: '8px' }}>
                  {t('heroCTA')}
                </Button>
                <SampleSecondaryLink
                  as="span"
                  onClick={goToProRegistration}
                  role="button"
                  tabIndex={0}
                  style={{ marginTop: 16 }}>
                  {t('heroSecondary')} →
                </SampleSecondaryLink>
              </HeroCTARow>
            </HeroTextColumn>
          </HeroFlex>
        </HeroInner>
      </HeroSection>

      {/* ── Methodology strip ────────────────────────────────────── */}
      <MethodSection>
        <MethodInner>
          <MethodCol>
            <MethodColTitle>{t('methodCol1Title')}</MethodColTitle>
            <MethodColItems>{t('methodCol1Items')}</MethodColItems>
          </MethodCol>
          <MethodCol>
            <MethodColTitle>{t('methodCol2Title')}</MethodColTitle>
            <MethodColItems>{t('methodCol2Items')}</MethodColItems>
          </MethodCol>
          <MethodCol>
            <MethodColTitle>{t('methodCol3Title')}</MethodColTitle>
            <MethodColItems>{t('methodCol3Items')}</MethodColItems>
          </MethodCol>
        </MethodInner>
      </MethodSection>

      {/* ── Sample Report (centerpiece) ──────────────────────────── */}
      <WhiteSection>
        <SectionInner>
          <SectionEyebrow>{t('sampleEyebrow')}</SectionEyebrow>
          <SampleGrid>
            <div>
              <SectionTitle large>{t('sampleTitle')}</SectionTitle>
              <SectionSubtitle>{t('sampleBody')}</SectionSubtitle>
              <SampleBullets>
                {[t('sampleBullet1'), t('sampleBullet2'), t('sampleBullet3'), t('sampleBullet4')].map((b) => (
                  <SampleBullet key={b}>
                    <SampleCheckIcon><Check size={13} strokeWidth={3} /></SampleCheckIcon>
                    {b}
                  </SampleBullet>
                ))}
              </SampleBullets>
              <SampleCTARow>
                <PrimaryActionButton onClick={openDialog}>
                  {t('sampleCTAPrimary')}
                </PrimaryActionButton>
                <SampleSecondaryLink as="span" onClick={goToProRegistration} role="button" tabIndex={0}>
                  {t('sampleCTASecondary')} →
                </SampleSecondaryLink>
              </SampleCTARow>
            </div>

            <ReportFrame role="img" aria-label={t('sampleImageAlt')}>
              <ReportLabel>{t('sampleReportLabel')}</ReportLabel>
              <ReportName>{t('sampleReportName')}</ReportName>

              <ReportSection>
                <ReportSectionLabel>{t('sampleReportSection1')}</ReportSectionLabel>
                <RadarMock>
                  <RadarShape />
                </RadarMock>
              </ReportSection>

              <ReportSection>
                <ReportSectionLabel>{t('sampleReportSection2')}</ReportSectionLabel>
                <BarRow><BarLabel>{t('sampleReportBar1')}</BarLabel><BarTrack><BarFill pct={88} tone="lime" /></BarTrack></BarRow>
                <BarRow><BarLabel>{t('sampleReportBar2')}</BarLabel><BarTrack><BarFill pct={72} tone="lime" /></BarTrack></BarRow>
                <BarRow><BarLabel>{t('sampleReportBar3')}</BarLabel><BarTrack><BarFill pct={54} tone="mid" /></BarTrack></BarRow>
                <BarRow><BarLabel>{t('sampleReportBar4')}</BarLabel><BarTrack><BarFill pct={36} tone="dim" /></BarTrack></BarRow>
                <BarRow><BarLabel>{t('sampleReportBar5')}</BarLabel><BarTrack><BarFill pct={28} tone="dim" /></BarTrack></BarRow>
                <BarRow><BarLabel>{t('sampleReportBar6')}</BarLabel><BarTrack><BarFill pct={22} tone="dim" /></BarTrack></BarRow>
              </ReportSection>

              <ReportFooter>
                <span>{t('sampleReportFooterScope')}</span>
                <span>{t('sampleReportFooterPage')}</span>
              </ReportFooter>
            </ReportFrame>
          </SampleGrid>
        </SectionInner>
      </WhiteSection>

      {/* ── Dimensions ───────────────────────────────────────────── */}
      <WhiteSection>
        <SectionInner>
          <SectionEyebrow>{t('dimsEyebrow')}</SectionEyebrow>
          <SectionTitle>{t('dimsTitle')}</SectionTitle>
          <SectionSubtitle>{t('dimsSubtitle')}</SectionSubtitle>

          <DimensionsGrid>
            {DIMENSIONS.map((d) => (
              <DimensionCard key={d.title}>
                <VizFrame><DimensionViz type={d.viz} /></VizFrame>
                <DimensionTitle>{d.title}</DimensionTitle>
                <DimensionText>{d.text}</DimensionText>
                <DimensionFramework>{d.framework}</DimensionFramework>
              </DimensionCard>
            ))}
          </DimensionsGrid>
        </SectionInner>
      </WhiteSection>

      {/* ── Session scenarios ────────────────────────────────────── */}
      <OffWhiteSection>
        <SectionInner>
          <SectionEyebrow>{t('scenarioEyebrow')}</SectionEyebrow>
          <SectionTitle>{t('scenarioTitle')}</SectionTitle>
          <SectionSubtitle>{t('scenarioSubtitle')}</SectionSubtitle>

          <ScenarioGrid>
            {SCENARIOS.map((s) => (
              <ScenarioCard key={s.title}>
                <ScenarioIconWrap><s.Icon size={22} /></ScenarioIconWrap>
                <ScenarioTitle>{s.title}</ScenarioTitle>
                <ScenarioText>{s.text}</ScenarioText>
              </ScenarioCard>
            ))}
          </ScenarioGrid>
        </SectionInner>
      </OffWhiteSection>

      {/* ── How it works (dark canyon #1) ────────────────────────── */}
      <DarkSection>
        <SectionInner>
          <SectionEyebrow dark>{t('stepsEyebrow')}</SectionEyebrow>
          <SectionTitle dark>{t('stepsTitle')}</SectionTitle>
          <SectionSubtitle dark>{t('stepsSubtitle')}</SectionSubtitle>

          <StepsGrid>
            {STEPS.map((s) => (
              <StepCard key={s.n}>
                <StepNumber>{s.n}</StepNumber>
                <StepTitle>{s.title}</StepTitle>
                <StepText>{s.text}</StepText>
              </StepCard>
            ))}
          </StepsGrid>

          <StepsFooter>{t('stepsFooter')}</StepsFooter>
        </SectionInner>
      </DarkSection>

      {/* ── Pricing ──────────────────────────────────────────────── */}
      <WhiteSection>
        <SectionInner>
          <PricingGrid>
            <div>
              <SectionEyebrow>{t('pricingEyebrow')}</SectionEyebrow>
              <SectionTitle style={{ marginBottom: 20 }}>{t('pricingTitle')}</SectionTitle>
              <PricingLeftBody>{t('pricingLeftBody', { price, currency: formatCurrencyDisplay(currency, getLocale()) })}</PricingLeftBody>
              <PricingLeftLink onClick={openDialog} role="button" tabIndex={0}>
                {t('pricingLeftCTA')} →
              </PricingLeftLink>
            </div>

            <PriceCard>
              <PriceAmount>{t('pricingPrice', { price, currency: formatCurrencyDisplay(currency, getLocale()) })}</PriceAmount>
              <PriceUnit>{t('pricingUnit')}</PriceUnit>
              <PriceList>
                {[t('pricingInclude1'), t('pricingInclude2'), t('pricingInclude3')].map((it) => (
                  <PriceListItem key={it}>
                    <Check size={16} style={{ flexShrink: 0, marginTop: 3, color: 'rgba(121,201,18,1)' }} />
                    {it}
                  </PriceListItem>
                ))}
              </PriceList>
              <PrimaryActionButton fullWidth onClick={goToPurchase}>
                {t('pricingCTA')}
              </PrimaryActionButton>
              <PriceSecondaryRow>
                <SampleSecondaryLink as="span" onClick={goToProRegistration} role="button" tabIndex={0}>
                  {t('pricingSecondary')} →
                </SampleSecondaryLink>
              </PriceSecondaryRow>
            </PriceCard>
          </PricingGrid>
        </SectionInner>
      </WhiteSection>

      {/* ── Final CTA ────────────────────────────────────────────── */}
      <CTASection>
        <CTAInner>
          <CTATitle>{t('ctaTitle')}</CTATitle>
          <CTASubtitle>{t('ctaSubtitle')}</CTASubtitle>
          <PrimaryActionButton onClick={openDialog}>
            {t('ctaButton')}
          </PrimaryActionButton>
          <CTASecondaryRow>
            {t('ctaSecondary')}
            <a
              href="/assessment/purchase"
              onClick={(e) => { e.preventDefault(); goToPurchase(); }}
            >
              {t('ctaSecondaryLink')} →
            </a>
          </CTASecondaryRow>
        </CTAInner>
      </CTASection>

      <Footer />

      <LicensePurchaseDialog open={dialogOpen} onClose={closeDialog} />
    </MainLayout>
  );
}
